import React from "react";
import { datasetColorVar } from "@/lib/datasetColor";

const SIZE = {
  sm: "h-1.5 w-1.5",
  md: "h-2 w-2",
  lg: "h-2.5 w-2.5",
} as const;

/// A space's swatch; `color` is its palette name (`spaceColor(space)`).
export const SpaceDot: React.FC<{
  color: string;
  size?: keyof typeof SIZE;
  className?: string;
}> = ({ color, size = "md", className }) => (
  <span
    aria-hidden
    className={`${SIZE[size]} shrink-0 rounded-app${className ? ` ${className}` : ""}`}
    style={{ backgroundColor: datasetColorVar(color) }}
  />
);

/// A dot that expands into the space's name on hover; `srLabel` is what a
/// screen reader hears in its place.
export const SpaceDotLabel: React.FC<{
  name: string;
  color: string;
  srLabel?: string;
}> = ({ name, color, srLabel = name }) => (
  // Positioned, so its sr-only text is placed in the row: inside a scroller it
  // would otherwise sit on the scroller's frame and stretch what holds it.
  <span
    className="group/space relative flex shrink-0 items-center font-mono text-fg-muted"
    style={{ fontSize: "var(--font-size-x-small)" }}
    title={name}
  >
    <SpaceDot color={color} size="sm" />
    <span className="sr-only">{srLabel}</span>
    {/* The leading gap lives inside the clipped box, so the collapsed state
        is exactly the dot — no dead space in the row. */}
    <span
      aria-hidden
      className="max-w-0 overflow-hidden pl-1.5 text-ellipsis whitespace-nowrap opacity-0 group-hover/space:max-w-32 group-hover/space:opacity-75"
      style={{
        transitionProperty: "max-width, opacity",
        transitionDuration: "var(--duration-short)",
        transitionTimingFunction: "var(--ease-enter)",
      }}
    >
      {name}
    </span>
  </span>
);
